import cx from 'classnames';
import type { MouseEvent, VoidFunctionComponent } from 'react';

import Badge from '../../generic/Badge/Badge';
import Button from '../../generic/Button/Button';
import NavigationButton from '../../generic/Button/NavigationButton';
import Icon, { IconText } from '../../generic/Icon';
import RouterLink from '../../generic/Link/RouterLink';
import type { IconClassName } from '../../generic/Icon';

import styles from './BulkImportPageCard.module.less';

type Props = {
  /**
   * Title for this section
   */
  title: string;
  /**
   * Should the "recommended" label/badge be displayed?
   */
  isRecommended?: boolean;
  /**
   * Where the navigation button/link should direct the user to.
   */
  linkTo: string;
  /**
   * The "feature icon(s)" to be rendered.
   */
  featureIcons: IconClassName[];
  /**
   * The main description section for the card.
   */
  description: string;
  /**
   * Text for the navigation button.
   */
  buttonText: string;
  /**
   * The subtitle is displayed below the navigation button.
   */
  subtitle?: string;
  /**
   * Icon prefix for the subtitle line.
   */
  subtitleIcon?: IconClassName;
  /**
   * If the feature should be de-emphasized, so we can upsell them.
   */
  shouldFeatureBeDeEmphasized?: boolean;
  /**
   * If present, a regular button is used instead of a Navigation button, using this as the click handler.
   */
  buttonOnClick?: (event: MouseEvent) => void;
};

/**
 * BulkImportPageCard displays a card representing an option
 * for getting a user's project imported into FOSSA.
 */
const BulkImportPageCard : VoidFunctionComponent<Props> = ({
  title,
  isRecommended,
  linkTo,
  featureIcons,
  description,
  buttonText,
  subtitle,
  subtitleIcon,
  shouldFeatureBeDeEmphasized,
  buttonOnClick,
}) => {
  return (
    <div className={styles.card}>
      <header className={styles.header}>
        {title}
        {isRecommended && (
          <div className={styles.recommendedBadge}>
            <Badge color="green">
              Recommended
            </Badge>
          </div>
        )}
      </header>
      <div className={styles.cardBody}>
        {shouldFeatureBeDeEmphasized ? (
          <span className={cx(styles.featureIcons, { [styles.premiumOnly]: shouldFeatureBeDeEmphasized })}>
            {featureIcons.map(icon => <Icon key={icon} icon={icon} />)}
          </span>
        ) : (
          <RouterLink
            className={cx(styles.featureIcons, { [styles.premiumOnly]: shouldFeatureBeDeEmphasized })}
            to={linkTo}
          >
            {featureIcons.map(icon => (
              <Icon key={icon} icon={icon} />
            ))}
          </RouterLink>
        )}
        <p className={cx(styles.description, { [styles.premiumOnly]: shouldFeatureBeDeEmphasized })}>
          {description}
        </p>
        {buttonOnClick ? (
          <Button
            type="secondary"
            size="medium"
            onClick={buttonOnClick}
          >
            {buttonText}
          </Button>
        ) : (
          <NavigationButton
            type="secondary"
            size="medium"
            to={linkTo}
          >
            {buttonText}
          </NavigationButton>
        )}
        {subtitle && (
          <p className={cx(styles.subtitle, { [styles.subtitleRecommended]: isRecommended })}>
            <IconText
              text={subtitle}
              icon={subtitleIcon}
            />
          </p>
        )}
      </div>
    </div>
  );
};

export default BulkImportPageCard;
